import React, { useCallback } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { FormattedMessage } from 'react-intl'
import { compact } from 'lodash'

import lmToConceptDict from 'data/output/lm-to-concept-dict.json'
import links from 'data/output/concept-deduped-links.json'

import { ParagraphSmallCaps, Paragraph1Regular, Paragraph1 } from 'components/Paragraphs'
import FormatNumber from 'common/utils/number'

import Link from './Link'

const InfosNodeContainer = styled(Paragraph1Regular)(({ theme, isSpecial, isPrimary }) => {
  const fontColor = isSpecial && isPrimary ? theme.tonic : theme.white

  return css`
    opacity: ${theme.opacity};
    color: ${fontColor};
    margin-left: auto;
    white-space: nowrap;
`
})

const InfosNode = ({ nodeId, isSpecial, isPrimary }) => {
  const nodes = lmToConceptDict[nodeId]
  const dict = nodes.reduce((p, c) => {
    p[c] = 1
    return p
  }, {})
  const nodeLinks = links.reduce((p, c) => {
    if (dict[c.source] && dict[c.target]) p.push(c.id)
    return p
  }, [])

  return (
    <InfosNodeContainer isSpecial={isSpecial} isPrimary={isPrimary}>
      <FormattedMessage
        id='list-node-infos'
        values={{
          nodesFormated: FormatNumber(nodes.length),
          relationsFormated: FormatNumber(nodeLinks.length),
          nodes: nodes.length,
          relations: nodeLinks.length
        }}
      />
    </InfosNodeContainer>
  )
}

const Losange = styled('span')`
  width: 8px;
  height: 8px;
  background-color: ${({ isPrimary, theme }) => isPrimary ? theme.tonic : 'transparent'};
  border: 1px solid ${({ isPrimary, theme }) => isPrimary ? theme.tonic : theme.white};
  transform: rotate(45deg);
  transform-origin: 70% 70%;
  margin-right: 15px;
`

const LineContainer = styled.div(({ isSpecial, isPrimary, theme, isCategoryLine }) => {
  const separatorColor = isSpecial
    ? (isPrimary ? theme.tonic : theme.white)
    : theme.separator
  const fontColor = isSpecial && isPrimary ? theme.tonic : theme.white

  return css`
    display: flex;
    align-items: center;
    border-bottom-style: solid;
    border-width: 1px;
    border-bottom-color: ${separatorColor};
    color: ${fontColor};
    cursor: pointer;
    padding-left: ${isSpecial || isCategoryLine ? 0 : 10}px;
    height: ${isCategoryLine ? 40 : 30}px;
    :hover {
      opacity: 0.6;
    }
  `
})

const CategoryLineText = styled(ParagraphSmallCaps)`
  text-transform: uppercase;
  text-decoration: none;
  opacity: ${({ opacify, theme, isHighlighted, isSelected }) => opacify && !isHighlighted && !isSelected ? theme.labelOpacity : 1};
  margin-right: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LineText = styled(Paragraph1)`
  line-height: 30px;
  text-decoration: none;
  margin-right: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Line = ({ data, isCategoryLine, selection, setSelection }) => {
  const isHighlighted = selection.filter((d) => !d.isFixed).map((d) => d.id).includes(data?.id)
  const isSelected = selection.filter((d) => d.isFixed).map((d) => d.id).includes(data?.id)

  // Is on hover or selected
  const isSpecial = isHighlighted || isSelected
  const isPrimary = selection.length && selection[0].id === data?.id
  const TextComponent = isCategoryLine ? CategoryLineText : LineText
  const showInfosNode = data?.nature && data?.nature !== 'concept' && isHighlighted && !isSelected

  const onClick = useCallback((e) => {
    e.stopPropagation()
    const newItem = !isSelected && {
      id: data.id,
      isFixed: true
    }
    setSelection(compact([
      selection.find((d) => d.id !== data?.id && d.isFixed),
      newItem
    ]))
  }, [setSelection, selection, data?.id, isSelected])

  const onMouseEnter = useCallback(() => {
    if (isSelected || selection.filter((d) => d.isFixed).length === 2) return

    const newItem = !isHighlighted && {
      id: data.id,
      isFixed: false
    }

    const newSelection = compact([
      ...selection.filter((d) => d.id !== data?.id && d.isFixed),
      newItem
    ])

    setSelection(newSelection)
  }, [setSelection, selection, data?.id, isHighlighted, isSelected])

  const onMouseLeave = useCallback(() => {
    const newSelection = selection.filter((d) => d.isFixed)
    setSelection(newSelection)
  }, [setSelection, selection])

  return (
    <LineContainer
      isCategoryLine={isCategoryLine}
      isSpecial={isSpecial}
      isPrimary={isPrimary}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {
        isSpecial && <Losange isPrimary={isPrimary} />
      }
      {
        isCategoryLine && data.order && (
          <TextComponent>
            {data.order}.
          </TextComponent>
        )
      }
      <TextComponent opacify isHighlighted={isHighlighted} isSelected={isSelected}>
        {data?.label}
      </TextComponent>
      {
        isSelected && <Link nodeId={data?.id} />
      }
      {
        showInfosNode && (
          <InfosNode
            nodeId={data?.id}
            isCategoryLine={isCategoryLine}
            isSpecial={isSpecial}
            isPrimary={isPrimary}
          />)
      }
    </LineContainer>
  )
}

export default Line
